import React, { useState } from "react";
import {
  Table,
  Badge,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from "reactstrap";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaEllipsisV,
  FaGift,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import {
  ApiDeleteUsuarioNavidad,
  ApiEntregarCanasta,
} from "../../../api/navidad";

const UsuariosNavidadTable = ({
  usuarios,
  onEdit,
  onUpdateList,
  loading,
  totalUsuarios,
  currentPage,
  pageSize,
  onPageChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEntregaModal, setShowEntregaModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [entregando, setEntregando] = useState(false);
  const [codigoEncargado, setCodigoEncargado] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const toggleDropdown = (userId) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleEdit = (user) => {
    onEdit(user);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const handleEntregarCanasta = (user) => {
    setSelectedUser(user);
    setCodigoEncargado("");
    setObservaciones("");
    setShowEntregaModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;

    setDeleting(true);
    try {
      const result = await ApiDeleteUsuarioNavidad({
        pre_usuario_navidad_id: selectedUser.pre_usuario_navidad_id,
      });

      if (result.sms === "ok") {
        toast.success("Usuario eliminado correctamente");
        onUpdateList();
        setShowDeleteModal(false);
        setSelectedUser(null);
      } else {
        toast.error(result.mensaje || "Error al eliminar el usuario");
      }
    } catch (error) {
      toast.error("Error al eliminar el usuario");
      console.error("Error:", error);
    } finally {
      setDeleting(false);
    }
  };

  const confirmEntregaCanasta = async () => {
    if (!selectedUser || !codigoEncargado.trim()) {
      toast.error("El código del encargado es requerido");
      return;
    }

    setEntregando(true);
    try {
      const result = await ApiEntregarCanasta({
        pre_usuario_navidad_id: selectedUser.pre_usuario_navidad_id,
        codigo_encargado: codigoEncargado.trim(),
        observaciones: observaciones.trim() || null,
        fecha_entrega: new Date().toISOString(),
      });

      if (result.sms === "ok") {
        toast.success("Canasta entregada correctamente");
        onUpdateList();
        setShowEntregaModal(false);
        setSelectedUser(null);
        setCodigoEncargado("");
        setObservaciones("");
      } else {
        toast.error(result.mensaje || "Error al entregar la canasta");
      }
    } catch (error) {
      toast.error("Error al entregar la canasta");
      console.error("Error:", error);
    } finally {
      setEntregando(false);
    }
  };

  const getEstadoBadge = (user) => {
    if (!user.estado) {
      return <Badge color="danger">Inactivo</Badge>;
    }
    if (!user.activo) {
      return <Badge color="warning">Desactivado</Badge>;
    }
    return <Badge color="success">Activo</Badge>;
  };

  const getCanastasBadge = (user) => {
    if (user.recibio_canasta) {
      return <Badge color="success">Entregada</Badge>;
    }
    return <Badge color="warning">Pendiente</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2 text-muted">Cargando usuarios...</p>
      </div>
    );
  }

  if (usuarios.length === 0) {
    return (
      <div className="text-center py-5">
        <FaUser size={48} className="text-muted mb-3" />
        <h5 className="text-muted">No hay usuarios registrados</h5>
        <p className="text-muted">
          Comienza agregando usuarios o cargando un archivo Excel
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="table-responsive">
        <Table hover className="mb-0">
          <thead className="table-dark">
            <tr>
              <th>Usuario</th>
              <th>Código</th>
              <th>Nombres</th>
              <th>Apellidos</th>
              <th>Cargo</th>
              <th>Área</th>
              <th>Canastas</th>
              <th>Estado</th>
              <th width="100">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((user, index) => (
              <motion.tr
                key={user.pre_usuario_navidad_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <td>
                  <div className="d-flex align-items-center">
                    <FaUser className="text-primary me-2" size={14} />
                    <span className="font-monospace">{user.usuario}</span>
                  </div>
                </td>
                <td>
                  <Badge color="info" className="font-monospace">
                    {user.code}
                  </Badge>
                </td>
                <td>
                  <strong>{user.nombres}</strong>
                </td>
                <td>
                  <strong>{user.apellidos}</strong>
                </td>
                <td>
                  {user.cargo ? (
                    <div className="d-flex align-items-center">
                      <FaBriefcase className="text-muted me-1" size={12} />
                      <span>{user.cargo}</span>
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {user.area ? (
                    <div className="d-flex align-items-center">
                      <FaBuilding className="text-muted me-1" size={12} />
                      <span>{user.area}</span>
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{getCanastasBadge(user)}</td>
                <td>{getEstadoBadge(user)}</td>
                <td>
                  <Dropdown
                    isOpen={dropdownOpen[user.pre_usuario_navidad_id] || false}
                    toggle={() => toggleDropdown(user.pre_usuario_navidad_id)}
                  >
                    <DropdownToggle
                      color="outline-secondary"
                      size="sm"
                      className="border-0"
                    >
                      <FaEllipsisV />
                    </DropdownToggle>
                    <DropdownMenu end>
                      <DropdownItem onClick={() => handleViewDetails(user)}>
                        <FaEye className="me-2" />
                        Ver Detalles
                      </DropdownItem>
                      <DropdownItem onClick={() => handleEdit(user)}>
                        <FaEdit className="me-2" />
                        Editar
                      </DropdownItem>
                      {!user.recibio_canasta && (
                        <DropdownItem
                          onClick={() => handleEntregarCanasta(user)}
                        >
                          <FaGift className="me-2" />
                          Entregar Canasta
                        </DropdownItem>
                      )}
                      <DropdownItem divider />
                      <DropdownItem
                        onClick={() => handleDelete(user)}
                        className="text-danger"
                      >
                        <FaTrash className="me-2" />
                        Eliminar
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Modal de detalles */}
      <Modal
        isOpen={showDetailModal}
        toggle={() => setShowDetailModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowDetailModal(false)}>
          <div className="d-flex align-items-center">
            <FaUser className="me-2 text-primary" />
            Detalles del Usuario
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <div className="row">
              <div className="col-md-6">
                <h6 className="text-primary mb-3">Información Personal</h6>
                <div className="mb-2">
                  <strong>Usuario/Cédula:</strong> {selectedUser.usuario}
                </div>
                <div className="mb-2">
                  <strong>Código:</strong> {selectedUser.code}
                </div>
                <div className="mb-2">
                  <strong>Nombres:</strong> {selectedUser.nombres}
                </div>
                <div className="mb-2">
                  <strong>Apellidos:</strong> {selectedUser.apellidos}
                </div>
                {selectedUser.correo && (
                  <div className="mb-2">
                    <strong>Email:</strong>
                    <div className="d-flex align-items-center">
                      <FaEnvelope className="text-muted me-1" size={12} />
                      {selectedUser.correo}
                    </div>
                  </div>
                )}
                {selectedUser.telefono && (
                  <div className="mb-2">
                    <strong>Teléfono:</strong>
                    <div className="d-flex align-items-center">
                      <FaPhone className="text-muted me-1" size={12} />
                      {selectedUser.telefono}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <h6 className="text-primary mb-3">Información Laboral</h6>
                {selectedUser.cargo && (
                  <div className="mb-2">
                    <strong>Cargo:</strong>
                    <div className="d-flex align-items-center">
                      <FaBriefcase className="text-muted me-1" size={12} />
                      {selectedUser.cargo}
                    </div>
                  </div>
                )}
                {selectedUser.area && (
                  <div className="mb-2">
                    <strong>Área:</strong>
                    <div className="d-flex align-items-center">
                      <FaBuilding className="text-muted me-1" size={12} />
                      {selectedUser.area}
                    </div>
                  </div>
                )}
                {selectedUser.direccion && (
                  <div className="mb-2">
                    <strong>Dirección:</strong>
                    <div className="d-flex align-items-center">
                      <FaMapMarkerAlt className="text-muted me-1" size={12} />
                      {selectedUser.direccion}
                    </div>
                  </div>
                )}
                <div className="mb-2">
                  <strong>Estado:</strong> {getEstadoBadge(selectedUser)}
                </div>
              </div>
              <div className="mb-2">
                <strong>Observaciones:</strong> {selectedUser.observaciones || "-"}
              </div>
              {selectedUser.pre_canastas_navidad &&
                selectedUser.pre_canastas_navidad.length > 0 && (
                  <div className="col-12 mt-3">
                    <h6 className="text-primary mb-3">Historial de Canastas</h6>
                    <div className="table-responsive">
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Año</th>
                            <th>Estado</th>
                            <th>Fecha Entrega</th>
                            <th>Entregado por</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedUser.pre_canastas_navidad.map(
                            (canasta, index) => (
                              <tr key={index}>
                                <td>{canasta.año}</td>
                                <td>
                                  <Badge
                                    color={
                                      canasta.estado_entrega === "ENTREGADA"
                                        ? "success"
                                        : canasta.estado_entrega === "PENDIENTE"
                                        ? "warning"
                                        : "info"
                                    }
                                  >
                                    {canasta.estado_entrega}
                                  </Badge>
                                </td>
                                <td>
                                  {canasta.fecha_entrega
                                    ? new Date(
                                        canasta.fecha_entrega
                                      ).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td>{canasta.entregado_por || "-"}</td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDetailModal(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de entrega de canasta */}
      <Modal
        isOpen={showEntregaModal}
        toggle={() => setShowEntregaModal(false)}
      >
        <ModalHeader toggle={() => setShowEntregaModal(false)}>
          <div className="d-flex align-items-center">
            <FaGift className="me-2 text-success" />
            Entregar Canasta Navideña
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedUser && (
            <div>
              <Alert color="info">
                <strong>Usuario:</strong> {selectedUser.nombres}{" "}
                {selectedUser.apellidos}
                <br />
                <strong>Código:</strong> {selectedUser.code}
                <br />
                <strong>Cédula:</strong> {selectedUser.usuario}
              </Alert>

              <div className="mb-3">
                <label className="form-label">
                  <strong>Código del Encargado *</strong>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ingrese el código del encargado"
                  value={codigoEncargado}
                  onChange={(e) => setCodigoEncargado(e.target.value)}
                  required
                />
                <small className="text-muted">
                  Código del usuario encargado de la entrega
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  <strong>Observaciones</strong>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Observaciones sobre la entrega (opcional)"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="outline-secondary"
            onClick={() => setShowEntregaModal(false)}
            disabled={entregando}
          >
            Cancelar
          </Button>
          <Button
            color="success"
            onClick={confirmEntregaCanasta}
            disabled={entregando || !codigoEncargado.trim()}
            className="d-flex align-items-center"
          >
            {entregando ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Entregando...</span>
                </div>
                Entregando...
              </>
            ) : (
              <>
                <FaGift className="me-2" />
                Entregar Canasta
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(false)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(false)}>
          <div className="d-flex align-items-center text-danger">
            <FaTrash className="me-2" />
            Confirmar Eliminación
          </div>
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>¿Estás seguro de que deseas eliminar este usuario?</strong>
          </Alert>
          {selectedUser && (
            <div>
              <p>
                <strong>Usuario:</strong> {selectedUser.usuario}
              </p>
              <p>
                <strong>Nombre:</strong> {selectedUser.nombres}{" "}
                {selectedUser.apellidos}
              </p>
              <p>
                <strong>Código:</strong> {selectedUser.code}
              </p>
              <p className="text-muted">
                Esta acción no se puede deshacer. El usuario será marcado como
                inactivo.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={deleting}
            className="d-flex align-items-center"
          >
            {deleting ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Eliminando...</span>
                </div>
                Eliminando...
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                Eliminar
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Paginación */}
      {totalUsuarios > pageSize && (
        <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
          <small className="text-muted">
            Mostrando {(currentPage - 1) * pageSize + 1} -{" "}
            {Math.min(currentPage * pageSize, totalUsuarios)} de {totalUsuarios}{" "}
            usuarios
          </small>
          <div className="d-flex align-items-center gap-2">
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              Primera
            </Button>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </Button>

            {/* Páginas inteligentes */}
            <div className="d-flex gap-1">
              {(() => {
                const totalPages = Math.ceil(totalUsuarios / pageSize);
                const pages = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxVisiblePages / 2)
                );
                let endPage = Math.min(
                  totalPages,
                  startPage + maxVisiblePages - 1
                );

                // Ajustar si estamos cerca del final
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                // Primera página si no está visible
                if (startPage > 1) {
                  pages.push(
                    <Button
                      key={1}
                      color="outline-secondary"
                      size="sm"
                      onClick={() => onPageChange(1)}
                    >
                      1
                    </Button>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 text-muted">
                        ...
                      </span>
                    );
                  }
                }

                // Páginas visibles
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <Button
                      key={i}
                      color={
                        currentPage === i ? "primary" : "outline-secondary"
                      }
                      size="sm"
                      onClick={() => onPageChange(i)}
                    >
                      {i}
                    </Button>
                  );
                }

                // Última página si no está visible
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 text-muted">
                        ...
                      </span>
                    );
                  }
                  pages.push(
                    <Button
                      key={totalPages}
                      color="outline-secondary"
                      size="sm"
                      onClick={() => onPageChange(totalPages)}
                    >
                      {totalPages}
                    </Button>
                  );
                }

                return pages;
              })()}
            </div>

            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === Math.ceil(totalUsuarios / pageSize)}
            >
              Siguiente
            </Button>
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => onPageChange(Math.ceil(totalUsuarios / pageSize))}
              disabled={currentPage === Math.ceil(totalUsuarios / pageSize)}
            >
              Última
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default UsuariosNavidadTable;
